import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Map, Marker, Popup, Circle, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import { v4 } from 'uuid';
import smoothscroll from 'smoothscroll-polyfill';

import FullscreenControl from 'react-leaflet-fullscreen';
import 'react-leaflet-fullscreen/dist/styles.css';
import 'font-awesome/css/font-awesome.min.css';
import 'leaflet.locatecontrol/dist/L.Control.Locate.min.css';

import 'leaflet.locatecontrol';
import "leaflet-rotatedmarker";

import 'mapbox-gl-leaflet';
import 'mapbox-gl';

import { THEME } from '../../configs/global';

import { getTime } from '../../helpers/Helpers';
import { getStopName } from './ServiceDetails';

import iconScrollTop from '../../assets/images/back_to_top.svg';

L.Icon.Default.imagePath =
  '//cdnjs.cloudflare.com/ajax/libs/leaflet/1.3.1/images/';

smoothscroll.polyfill();

const boundsOptions = { padding: [30, 30] };

const getIconPathFromActiveTheme = (iconName) => {
  switch (THEME.NAME) {
    case 'Megabus':
      return `megabus/${iconName}`;
    case 'WCCE':
      return `wcce/${iconName}`;  
    case 'NX-Bus':
      return `nxbus/${iconName}`;
    default:
      return iconName;
  }
};

const iconBigSize = [24, 40];
const iconDefaultSize = [14, 26];

export const showDueTimeStatus = (stop) => {
  if (!stop)
    return <span>Unknown</span>
  const currentTime = Math.round(Date.now() / 1000);
  if (stop.estimated_departure_time_unix && stop.estimated_departure_time_unix >= currentTime) {
    let mins = Math.round((stop.estimated_departure_time_unix - currentTime) / 60);
    return <span>{mins ? `Due ${mins} mins` : 'Due'}</span>
  } else if (stop.live_departure_time_unix) {
    //let mins = Math.round((stop.live_departure_time_unix - currentTime) / 60);
    //return <span>{mins ? `Due ${mins} mins` : 'Due'}</span>
    return <span>Departed</span>
  } else if (stop.scheduled_departure_time_unix >= currentTime) {
    let mins = Math.round((stop.scheduled_departure_time_unix - currentTime) / 60);
    return <span>{mins ? `Due ${mins} mins` : 'Due'}</span>
  } else
    return <span>Departed</span>
}
const createIcon = (name, size, format = 'png', anchor, popupanchor) =>
  new L.Icon({
    iconUrl: require(`../../assets/images/${name}.${format}`),
    iconRetinaUrl: require(`../../assets/images/${name}.${format}`),
    iconSize: size,
    iconAnchor: anchor ? anchor : null,
    popupAnchor: popupanchor ? popupanchor : [0, 0],
  });

const mapStopsToMarkers = (stops) => {
  const stopsLen = stops.length;
  return stops.map((stop, index) => {
    const obj = {
      lat: stop.wgs84_latitude_degrees,
      lng: stop.wgs84_longitude_degrees,
      name: stop[THEME.PAGES.DETAILS.STOP]
      ? stop[THEME.PAGES.DETAILS.STOP]
      : stop.public_facing_name
      ? stop.public_facing_name
      : stop.name
    };

    if (index === 0) {
      return {
        ...obj,
        icon: createIcon(
          getIconPathFromActiveTheme('stop-start'),
          iconBigSize,
          'svg',
          [12, 40],
          [1, -45]
        ),
      };
    }

    if (index + 1 === stopsLen) {
      return {
        ...obj,
        icon: createIcon(
          getIconPathFromActiveTheme('stop-finish'),
          iconBigSize,
          'svg',
          [12, 40],
          [1, -45]
        ),
      };
    }

    return {
      ...obj,
      icon: createIcon(
        getIconPathFromActiveTheme('stop'),
        iconDefaultSize,
        'svg',
        [7, 26],
        [0, -36]
      ),
    };
  });
};
  
const mapServicesToMarkers = (services) => {
  let filtered = [];
  for (let i = 0; i < services.length; i++) {
    let stop = services[i];
    if (!stop.active_vehicle)
      continue;

    if (stop.tracking.is_completed)
      continue;

    if (stop.from_stop.estimated_arrival_time_unix && stop.from_stop.estimated_arrival_time_unix < (Date.now() / 1000))
      continue;

    if (stop.from_stop.scheduled_arrival_time_unix < (Date.now() / 1000))
      continue;

    if (stop.from_stop.live_departure_time_unix && stop.from_stop.live_departure_time_unix < (Date.now() / 1000))
      continue;

    let existing = false;
    for (let fi = 0; fi < filtered.length; fi++) {
      if (stop.active_vehicle.current_wgs84_latitude_degrees === filtered[fi].active_vehicle.current_wgs84_latitude_degrees)
        existing = true;
    }

    if (!existing)
      filtered.push(stop);

  }
  let markers = filtered.map((stop, index) => {
    if (stop.from_stop.scheduled_arrival_time_unix > (Date.now() / 1000) + 3600)
      return null

    const obj = {
      lat: stop.active_vehicle.current_wgs84_latitude_degrees,
      lng: stop.active_vehicle.current_wgs84_longitude_degrees,
      name: stop.active_vehicle.last_update_time_formatted_local,
      data: stop
    };

    return {
      ...obj,
      icon: createIcon(
        getIconPathFromActiveTheme('bus-icon-in'),
        [38, 38],
        'svg',
        [19, 19],
        [1, -18]
      ),
    };
  });
  return markers.filter((marker) => marker != null);
};

const mapStateToProps = (state) => ({
  services: state.service.services,
});

class MapWithProvider extends Map {
  createLeafletElement(props) {
    const LeafletMapElement = super.createLeafletElement(props);
    L.control.locate().addTo(LeafletMapElement);
    //LeafletMapElement.on('viewreset', this.props.onZoom);
    LeafletMapElement.invalidateSize(
      LeafletMapElement.getBoundsZoom(
        this.props.fitbounds ? this.props.fitbounds : this.props.bounds
      )
    );

    L.mapboxGL({
      attribution:
        '<a href="https://www.maptiler.com/copyright/" target="_blank">© MapTiler</a> <a href="https://www.openstreetmap.org/copyright" target="_blank">© OpenStreetMap contributors</a>',
      accessToken:
        THEME.MAPTILER_KEY ? THEME.MAPTILER_KEY : 'pk.eyJ1IjoidXRyYWNrbWFwIiwiYSI6Im9iV245NnMifQ.ddr0eLkRuZUoNVglI8tZvg',
      style:
        'https://api.maptiler.com/maps/basic/style.json?key=' + (THEME.MAPSTYLE_KEY ? THEME.MAPSTYLE_KEY : 'Byaz3CMIZKoUmjEqSl1b'),
    }).addTo(LeafletMapElement);

    return LeafletMapElement;
  }
}

class ServiceMap extends Component {
  static propTypes = {
    srv: PropTypes.array.isRequired,
    showServiceDetails: PropTypes.func.isRequired,
  };

  state = {
    markers: [],
    bounds: [],
  };

  componentWillMount() {
    this.setState({
      splitpolylines: [],
      service: [],
      fit: 1,
    });
    this.setState({
      markers: mapServicesToMarkers(this.props.srv),
      stopsMarkers: mapStopsToMarkers(this.props.stops),
      bounds: L.latLngBounds(mapServicesToMarkers(this.props.srv)),
    });
  }

  topFunction() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  handleScroll() {
    if (
      document.body.scrollTop > 20 ||
      document.documentElement.scrollTop > 20
    ) {
      document.getElementById('topBtn').style.display = 'block';
    } else {
      document.getElementById('topBtn').style.display = 'none';
    }
  }

  componentDidMount() {
    window.addEventListener('scroll', this.handleScroll);
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
  }

  componentDidUpdate(prevProps) {
    if (this.mapRef) {
      if (
        (this.props.fitbounds &&
          prevProps.fitbounds !== this.props.fitbounds) ||
        this.state.fit
      ) {
        this.mapRef.leafletElement.invalidateSize();
        if (this.props.fitbounds) {
          this.mapRef.leafletElement.fitBounds(this.props.fitbounds, {
            padding: [30, 30],
          });
          this.setState({
            fit: 0,
          });
        } else if (this.state.bounds) {
          this.mapRef.leafletElement.fitBounds(this.state.bounds, {
            padding: [30, 30],
          });
          this.setState({
            fit: 0,
          });
        }
      }
      if (
        prevProps.location.pathname !== this.props.location.pathname ||
        prevProps.srv !== this.props.srv
      ) {
        this.setState({
          service: [],
        });
        const map = this.mapRef.leafletElement;
        map.invalidateSize();
        this.setState({
          markers: mapServicesToMarkers(this.props.srv),
          stopsMarkers: mapStopsToMarkers(this.props.stops),
          bounds: L.latLngBounds(mapServicesToMarkers(this.props.srv)),
        });
        setTimeout(() => {
          this.centerPolyline();
        }, 100);
      }
    }
  }

  centerPolyline = () => {
    const { bounds } = this.state;
    if (bounds && this.mapRef) {
      this.mapRef.leafletElement.invalidateSize();
    }
  };

  render() {
    const {
      markers,
      stopsMarkers,
      bounds,
    } = this.state;
    
    let {
      service
    } = this.state;

    if (!service.active_vehicle)
      service = this.props;

    const {
      showServiceDetails
    } = this.props;

    return (
      <MapWithProvider
        fullscreenControl
        bounds={this.props.fitbounds ? this.props.fitbounds : bounds}
        boundsOptions={this.props.fitbounds ? boundsOptions : null}
        ref={(ref) => (this.mapRef = ref)}
        path={(CLIP_PADDING) => 1}
        maxZoom={18}
      >
        {false && FullscreenControl}

        {markers.map((marker) => (
          marker && marker.lat && <Fragment key={v4()}>
            <Marker
              position={marker.lat ? marker : [marker.wgs84_latitude_degrees, marker.wgs84_longitude_degrees]}
              rotationAngle={marker.data.active_vehicle.current_forward_azimuth_degrees}
              icon={createIcon(
                getIconPathFromActiveTheme('bus-icon-out'),
                [38, 38],
                'svg',
                [19, 19],
                [1, -18]
              )}
            ></Marker>

            <Marker
              position={marker.lat ? marker : [marker.wgs84_latitude_degrees, marker.wgs84_longitude_degrees]}
              icon={marker.icon}
              //onClick={marker.data.tracking ? showServiceDetails(marker.data) : 'return false'}
            >
              <Popup className="alert-popup">
                <div className="alert-popup-header">
                  <div>
                    <span className="vehicle-route">
                      {`${marker.data.trip.route_id}`}
                    </span>
                    <span className="vehicle-time">
                      {showDueTimeStatus(marker.data.from_stop)}
                    </span>
                  </div>
                  <div className="highfrequency-route">High frequency route</div>
                </div>
                <h3>
                  {`${marker.data.trip.departure_location_name} → ${marker.data.trip.arrival_location_name}`}
                </h3>
                {THEME.SHOW_TRIPID && <span>{`Trip ID: ${marker.data.trip.id}`}<br /></span>}
                {`${marker.data.tracking.is_moving ? 'Moving' : 'Stationary'}`}
                <br />
                <span className="vehicle-update-time">
                  {`Last Updated: ${getTime(marker.data.active_vehicle.last_update_time_formatted_local)}`}
                </span>
                <a className="track" href="#track" onClick={marker.data.tracking ? showServiceDetails(marker.data, true) : 'return false'}>Track</a>
              </Popup>
            </Marker>
          </Fragment>
        ))}

        {stopsMarkers.map((marker) => (
          marker && marker.lat ?
          <Fragment key={v4()}>
            <Marker 
              position={marker}
              icon={marker.icon}
            >
              <Popup>
                {getStopName(marker.name)[0]}
                <br />
                {getStopName(marker.name).length > 1
                  ? getStopName(marker.name)[1]
                  : null}
              </Popup>
            </Marker>
            <Circle
              center={marker}
              radius={1000}
              color={'#f46e70'}
              pathOptions={{ color: '#f46e70' }}
              fill={false}
              weight={2}
            >
              <Tooltip>
                  1 km
              </Tooltip>
            </Circle>
            <Circle
              center={marker}
              radius={3000}
              color={'#f46e70'}
              pathOptions={{ color: '#f46e70' }}
              fill={false}
              weight={1.3}
            >
              <Tooltip>
                  3 km
              </Tooltip>
            </Circle>
            <Circle
              center={marker}
              radius={5000}
              color={'#f46e70'}
              pathOptions={{ color: '#f46e70' }}
              fill={false}
              weight={0.8}
            >
              <Tooltip>
                  5 km
              </Tooltip>
            </Circle>
          </Fragment> : <Fragment></Fragment>
        ))}

        <img
          id="topBtn"
          src={iconScrollTop}
          alt="Top"
          className="scrol-top"
          style={{ display: 'none' }}
          onClick={this.topFunction}
        />
      </MapWithProvider>
    );
  }
}

export default withRouter(connect(mapStateToProps, {})(ServiceMap));



// WEBPACK FOOTER //
// ./src/containers/service-list/ServiceMap.js